import React, { useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Container, Form, Offcanvas, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  type SubscriptionData,
  type SubscriptionVariant,
  type BoxData,
  type LocalizationContext,
} from '../../lib/types';
import { type RootState } from '../../store';
import { useAppContext } from '../../context/shared';
import { BoxCard } from '../BoxCard';
import styles from '../../styles/EditCartModal.module.css';
import { addToCartRequest } from '../../store/actions/cart';

type SubscriptionChangeModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly editFormSubscriptionTitle: string;
  readonly editFormBoxTitle: string;
  readonly subscriptions: SubscriptionData[];
};

const generateSubscriptionLabel = (
  subscription: SubscriptionData | undefined,
  localization: LocalizationContext
) => {
  return `${subscription?.title} • €${subscription?.monthlyPayment}${localization.cart_price_per_month}`;
};

export function EditCartDrawer(props: SubscriptionChangeModalProps) {
  const { localization } = useAppContext();
  const dispatch = useDispatch();

  const {
    isOpen,
    onClose,
    subscriptions,
    editFormBoxTitle,
    editFormSubscriptionTitle,
  } = props;

  const selectCart = (state: RootState) => state.cart;
  const selectBoxes = (state: RootState) => state.boxes as { data: BoxData[] };

  const selectCartAndBoxes = createSelector(
    selectCart,
    selectBoxes,
    (cart, boxes) => ({ cart, boxes })
  );
  const { cart, boxes } = useSelector(selectCartAndBoxes);

  const annualSubscription = subscriptions.find(
    (sbs) => sbs.frequency === 'annual'
  );
  const biannualSubscription = subscriptions.find(
    (sbs) => sbs.frequency === 'biannual'
  );
  const annualMonthlySubscription = subscriptions.find(
    (sbs) => sbs.frequency === 'annualMonthly'
  );
  const biannualMonthlySubscription = subscriptions.find(
    (sbs) => sbs.frequency === 'biannualMonthly'
  );
  const monthlySubscription = subscriptions.find(
    (sbs) => sbs.frequency === 'monthly'
  );

  const annualSubscriptionLabel = generateSubscriptionLabel(
    annualSubscription,
    localization
  );
  const biannualSubscriptionLabel = generateSubscriptionLabel(
    biannualSubscription,
    localization
  );
  const annualMonthlySubscriptionLabel = generateSubscriptionLabel(
    annualMonthlySubscription,
    localization
  );
  const biannualMonthlySubscriptionLabel = generateSubscriptionLabel(
    biannualMonthlySubscription,
    localization
  );
  const monthlySubscriptionLabel = generateSubscriptionLabel(
    monthlySubscription,
    localization
  );

  const selectedItem = cart.data?.items[0];

  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionVariant>(selectedItem?.subscription ?? 'monthly');

  const [selectedBoxId, setSelectedBoxId] = useState(selectedItem?.id ?? 0);

  const handleBoxChange = (
    newVariant: SubscriptionVariant,
    newBoxId: number
  ) => {
    const newBox = boxes.data.find((box) => box.id === newBoxId);

    if (newBox) {
      const replacementVariantId = newBox.variantsJson?.[newVariant].id;
      if (replacementVariantId) {
        dispatch(
          addToCartRequest({
            id: replacementVariantId,
            subscription: newVariant,
            redirect: false,
          })
        );
      }
    }

    onClose();
  };

  return (
    <Offcanvas show={isOpen} bsPrefix="drawer" onHide={onClose}>
      <Offcanvas.Body>
        <Container className={`${styles.drawerContainer} py-3 px-3`}>
          <Row className={`${styles.drawerScrollable}`}>
            <h5 className="fw-normal py-3">{editFormSubscriptionTitle}</h5>
            <div className="py-2">
              <b className="my-2">
                {localization.cart_toggle_prepayment_label}
              </b>
              <Form.Check
                className="my-2"
                type="radio"
                label={annualSubscriptionLabel}
                checked={selectedSubscription === 'annual'}
                onChange={() => {
                  setSelectedSubscription('annual');
                }}
              />
              <Form.Check
                className="my-2"
                type="radio"
                label={biannualSubscriptionLabel}
                checked={selectedSubscription === 'biannual'}
                onChange={() => {
                  setSelectedSubscription('biannual');
                }}
              />
            </div>
            <div className="py-2">
              <b className="my-2">{localization.cart_toggle_monthly_label}</b>
              <Form.Check
                className="my-2"
                type="radio"
                label={annualMonthlySubscriptionLabel}
                checked={selectedSubscription === 'annualMonthly'}
                onChange={() => {
                  setSelectedSubscription('annualMonthly');
                }}
              />
              <Form.Check
                className="my-2"
                type="radio"
                label={biannualMonthlySubscriptionLabel}
                checked={selectedSubscription === 'biannualMonthly'}
                onChange={() => {
                  setSelectedSubscription('biannualMonthly');
                }}
              />
              <Form.Check
                className="my-2"
                type="radio"
                label={monthlySubscriptionLabel}
                checked={selectedSubscription === 'monthly'}
                onChange={() => {
                  setSelectedSubscription('monthly');
                }}
              />
            </div>
            <h5 className="fw-normal py-3">{editFormBoxTitle}</h5>
            <Container className="py-2">
              <Row xs={2} md={3} lg={4}>
                {[...boxes.data]
                  .sort(
                    (a, b) =>
                      (a.ageGroup?.minMonths ?? 0) -
                      (b.ageGroup?.minMonths ?? 0)
                  )
                  .map((box) => {
                    return (
                      <Col
                        key={box.id}
                        className={`${styles.boxContainer} p-3`}
                      >
                        <BoxCard
                          isSmall
                          box={box}
                          onClick={() => {
                            setSelectedBoxId(box.id);
                          }}
                        >
                          <FontAwesomeIcon
                            className={
                              styles.boxCheckmark +
                              ' ' +
                              (selectedBoxId === box.id && styles.selected)
                            }
                            icon={faCheck}
                            size="2x"
                          />
                        </BoxCard>
                      </Col>
                    );
                  })}
              </Row>
            </Container>
          </Row>

          <Row className={`${styles.drawerButtons}`}>
            <Col xs={12} md={7} lg={5} xl={4}>
              <Button
                className="w-100"
                variant="primary"
                onClick={() => {
                  handleBoxChange(selectedSubscription, selectedBoxId);
                }}
              >
                {localization.cart_item_change_subscription_modal_continue_btn}
              </Button>
            </Col>
          </Row>
        </Container>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
